import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InboxThreadFilters } from '../../types/inbox';
import { cn } from '../../lib/utils';
import { fetchAccountWorklist } from './api';
import { ChannelIcon } from '../../components/ui/ChannelIcon';

// Community Inbox — account worklist panel (brief §1). Lists communityEnabled
// accounts with their connected channels + a server-aggregated outstanding
// (unresolved, all-kinds) count badge, most-outstanding first. Clicking a row
// sets it as the active account filter (clicking the selected row clears it).
// Polls on the same ~15s cadence as the thread list so counts stay live.
//
// DECISION (0-outstanding visibility, brief §1 flag): accounts with nothing
// outstanding are HIDDEN by default behind a "show all" expander (keeps the
// working queue focused), and always sort to the bottom when shown.

export function AccountWorklistPanel({
  filters,
  selectedAccountId,
  onSelectAccount,
}: {
  filters: InboxThreadFilters;
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);

  // Strip the account selection from the worklist query — the panel drives that
  // filter, so it must always show the full (brand/AM/channel-narrowed) roster
  // regardless of which account is currently selected. Stripping also avoids a
  // needless refetch every time the selection changes.
  const worklistFilters = useMemo(() => {
    const { accountIds: _accountIds, ...rest } = filters;
    return rest;
  }, [filters]);

  const worklist = useQuery({
    queryKey: ['community-worklist', worklistFilters],
    queryFn: () => fetchAccountWorklist(worklistFilters),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });

  const rows = worklist.data?.accounts ?? [];
  const withOutstanding = useMemo(() => rows.filter((r) => r.outstandingCount > 0), [rows]);
  const zeroCount = rows.length - withOutstanding.length;
  const visible = showAll ? rows : withOutstanding;

  return (
    // shrink-0 is load-bearing: this panel sits inside the rail's
    // `overflow-y-auto` column, where flex children shrink by DEFAULT. Without
    // it the panel is squashed to the leftover height and its own
    // `overflow-hidden` silently CLIPS the account rows (unreachable — the rail
    // never scrolls because the panel "fits"). With shrink-0 the panel keeps its
    // full natural height and the RAIL scrolls — the "never truncate" behaviour.
    <div className="flex shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Accounts
        </span>
        {selectedAccountId && (
          <button
            type="button"
            onClick={() => onSelectAccount(selectedAccountId)}
            className="text-[11px] font-medium text-kyck-600 hover:text-kyck-700"
          >
            Clear
          </button>
        )}
      </div>
      {/* Part D3 — no inner scroll cap: the panel grows to fit ALL accounts
          (the whole left rail scrolls instead), and names show in full. */}
      <div>
        {worklist.isLoading && rows.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-slate-400">Loading…</p>
        )}
        {!worklist.isLoading && rows.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            No community accounts match the current filters.
          </p>
        )}
        {!worklist.isLoading && rows.length > 0 && visible.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            Nothing outstanding — inbox zero.
          </p>
        )}
        {visible.map((row) => {
          const selected = selectedAccountId === row.accountId;
          // Part A — the badge is the total UNRESOLVED count; a Priority item on
          // the account emphasises it (amber) for the "check before you leave"
          // surfacing, otherwise it's the neutral green worklist bubble.
          const hasPriority = row.priorityCount > 0;
          return (
            <button
              key={row.accountId}
              type="button"
              onClick={() => onSelectAccount(row.accountId)}
              className={cn(
                'flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-slate-50',
                hasPriority && 'border-l-2 border-l-amber-400',
                selected && 'bg-kyck-50/70 hover:bg-kyck-50/70',
              )}
            >
              <span className="flex shrink-0 items-center gap-0.5">
                {row.channels.length > 0 ? (
                  row.channels.map((c) => <ChannelIcon key={c} channel={c} className="h-3.5 w-3.5" />)
                ) : (
                  <span className="h-3.5 w-3.5" />
                )}
              </span>
              <span
                className={cn(
                  'min-w-0 flex-1 break-words text-sm leading-snug',
                  hasPriority ? 'font-semibold text-slate-900' : 'text-slate-700',
                )}
              >
                {row.name}
              </span>
              {row.outstandingCount > 0 && (
                <span
                  title={
                    hasPriority
                      ? `${row.outstandingCount} outstanding · ${row.priorityCount} priority`
                      : `${row.outstandingCount} outstanding`
                  }
                  className={cn(
                    'flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                    hasPriority ? 'bg-amber-500 text-white' : 'bg-kyck-500 text-white',
                  )}
                >
                  {row.outstandingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {zeroCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="border-t border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700"
        >
          {showAll ? 'Hide accounts with none outstanding' : `Show ${zeroCount} with none outstanding`}
        </button>
      )}
    </div>
  );
}
